import React, { useEffect, useRef } from 'react';
import { Form, message } from 'antd';
import {
  useCreateEventAtOnceMutation,
  useUpdateEventAtOnceMutation,
  EventAttributes,
  EventTypeEnum,
  VirtualMeetingTypeEnum,
  namedOperations,
} from 'graphql/types';
import { useHistory, useLocation } from 'react-router-dom';
import { ResizeImageResult, ParseGoogleAddressResult } from 'helpers';
import StepBarForm from 'components/stepBarForm';
import moment from 'moment';
import {
  formatEventDateTimeInTimezone,
  getDefaultEventTimezone,
} from 'utils/eventDateTime';
import { authorizeAndCreateGoogleMeet } from 'helpers/googleMeet';
import CoverPhotoStep from './coverPhotoStep';
import InformationStep from './informationStep';
import EventDetailsStep from './eventDetails';
import AddCategoriesStep from '../category/addCategoriesStep';
import { isGoogleMeetSetup, isVirtualEventType } from './virtualMeetingTypes';
import { RecurrenceTypeEnum, resolveRecurrenceForApi } from './recurringOptions';
import {
  coverPhotoFromUrl,
  CreateEventPrefillState,
  resolveCoverPhotoBlob,
} from './createEventPrefill';

interface FromFields {
  title: string;
  dates: moment.Moment[];
  timezone: string;
  description: string;
  group: string;
  coverPhoto: ResizeImageResult;
  address: ParseGoogleAddressResult;
  eventType: EventTypeEnum;
  eventUrl?: string;
  passcode?: string;
  virtualMeetingType?: VirtualMeetingTypeEnum;
  autoRecording?: boolean;
  price: number;
  capacity: number;
  categories: string[];
  recurringPattern?: RecurrenceTypeEnum;
  repeatDayInterval?: number;
}

const steps = [
  {
    title: 'Informations',
    pageTitle: 'Add Event Details',
    validation: ['title', 'description', 'group', 'dates', 'timezone'],
    content: InformationStep,
  },
  {
    title: 'Categories',
    pageTitle: 'Select related categories',
    validation: ['categories'],
    content: AddCategoriesStep,
  },
  { title: 'Cover Photo', pageTitle: 'Add Cover Photo', validation: ['coverPhoto'], content: CoverPhotoStep },
  {
    title: 'Extra Details',
    pageTitle: 'Add Extra Details',
    validation: ['price', 'eventType', 'address', 'virtualMeetingType', 'eventUrl', 'passcode'],
    content: EventDetailsStep,
  },
];

function CreateEvent() {
  const [form] = Form.useForm<FromFields>();
  const [createEvent, { loading }] = useCreateEventAtOnceMutation({
    refetchQueries: [namedOperations.Query.GetMyAccount],
  });
  const [updateEventDetails, { loading: generatingGoogleMeet }] = useUpdateEventAtOnceMutation();
  const history = useHistory();
  const location = useLocation<CreateEventPrefillState | undefined>();
  const prefillAppliedRef = useRef(false);

  useEffect(() => {
    const prefill = location.state;
    if (!prefill || prefillAppliedRef.current) {
      return;
    }
    prefillAppliedRef.current = true;

    const initial: Partial<FromFields> = {};
    if (prefill.groupId) {
      initial.group = prefill.groupId;
    }
    if (prefill.categoryIds?.length) {
      initial.categories = prefill.categoryIds;
    }
    if (prefill.coverPhotoUrl) {
      initial.coverPhoto = coverPhotoFromUrl(prefill.coverPhotoUrl);
    }
    if (Object.keys(initial).length) {
      form.setFieldsValue(initial);
    }
  }, [form, location.state]);

  const onFinish = async (values: FromFields) => {
    const {
      dates,
      timezone,
      virtualMeetingType,
      eventUrl,
      passcode,
      autoRecording,
      recurringPattern,
      repeatDayInterval,
      ...restValues
    } = values;
    const eventTimezone = timezone || getDefaultEventTimezone();
    const useAutoCreateZoom =
      isVirtualEventType(restValues.eventType) && virtualMeetingType === VirtualMeetingTypeEnum.AutoCreateZoom;
    const startTime = formatEventDateTimeInTimezone(dates[0], eventTimezone);
    const endTime = formatEventDateTimeInTimezone(dates[1], eventTimezone);

    const coverBlob = await resolveCoverPhotoBlob(values.coverPhoto);
    if (!coverBlob) {
      message.error('Please add a cover photo.');
      return;
    }

    const { recurrenceType, repeatInterval } = resolveRecurrenceForApi(repeatDayInterval, recurringPattern);

    const attributes: EventAttributes = {
      ...restValues,
      recurrenceType,
      repeatInterval,
      virtualMeetingType,
      coverPhoto: coverBlob,
      startTime,
      endTime,
      timezone: eventTimezone,
      ...(useAutoCreateZoom ? { autoRecording: Boolean(autoRecording) } : {}),
      ...(useAutoCreateZoom ? {} : { eventUrl, passcode }),
    };

    try {
      const result = await createEvent({
        variables: { input: attributes },
      });
      const eventId = result.data?.createEventAtOnce?.data?.id;
      if (eventId) {
        // The Meet is created from the browser once the event exists, then stored on it.
        // A failure here still leaves a usable event, so only warn about it.
        if (isGoogleMeetSetup(restValues.eventType, virtualMeetingType)) {
          try {
            const { hangoutLink } = await authorizeAndCreateGoogleMeet({
              summary: restValues.title,
              eventStartTime: startTime,
              eventEndTime: endTime,
              timezone: eventTimezone,
            });
            await updateEventDetails({
              variables: { eventId, input: { eventUrl: hangoutLink } },
            });
          } catch (err) {
            message.error(
              err instanceof Error && err.message && err.message !== 'access_denied'
                ? err.message
                : 'Event created, but the Google Meet could not be generated. Try the button on the event page.',
            );
          }
        }
        history.push(`/events/${eventId}`);
      }
    } catch (err) {
      if (err instanceof Error) {
        message.error(err.message);
      }
    }
  };

  const handlePageBack = () => {
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push('/account/events');
  };

  return (
    <StepBarForm<FromFields>
      pageTitle="Make new Event"
      form={form}
      onFinish={onFinish}
      loading={loading || generatingGoogleMeet}
      steps={steps}
      onPageBack={handlePageBack}
    />
  );
}

export default CreateEvent;
